import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * 筛选状态的 URL 同步（重构方案 §5.1）
 * ------------------------------------------------------------------
 * 目标：筛选 / 分页 / 二级 tab 之类「视图状态」写进 URL，刷新、分享、返回都能还原。
 *
 * 为什么不用路由的 history.replace：那会触发路由更新 → 页面重渲染 → 可能在依赖写得不严的
 * 地方引起取数重跑。这里直接用浏览器的 replaceState 改 query，**不动路由**，
 * 只替换当前历史项（不新增），与 ui/AnchorNav 写 hash 的做法一致。
 *
 * 约定：
 *   · 只把**非默认值**写进 URL（默认值不落 URL，地址栏保持干净、便于分享）；
 *   · 读回时以 URL 为准，缺字段回落默认值；数字型字段由调用方自行 Number 转换；
 *   · 监听 popstate：浏览器前进/后退时回读（这样才真的「返回可用」）。
 *
 * 用法（调用方按如下约定取用）：
 *   · 入参是默认值对象，同时决定哪些字段参与同步；
 *   · 返回 filters（已与 URL 同步的值）/ setFilter(key, value) / setAll(patch) / reset()；
 *   · setFilter('coursePage', 2) 改值即自动写 URL；filters.coursePage 读回时是字符串，
 *     数字型字段由调用方自行转换。
 *
 * @param defaults 默认值对象（同时决定哪些 key 参与同步）
 */
export const useUrlFilters = (defaults = {}) => {
  const keys = Object.keys(defaults);
  const keyStr = keys.join('|');

  const [filters, setFilters] = useState(() => readFromUrl(defaults, keys));

  /* 值变化 → 写 URL（replaceState，不新增历史项） */
  useEffect(() => {
    if (typeof window === 'undefined' || !window.history) return;
    const params = new URLSearchParams(window.location.search);
    keys.forEach((key) => {
      const value = filters[key];
      const isDefault =
        String(value) === String(defaults[key]) ||
        value === '' ||
        value === undefined ||
        value === null;
      if (isDefault) params.delete(key);
      else params.set(key, String(value));
    });
    const query = params.toString();
    const next = `${window.location.pathname}${query ? `?${query}` : ''}${
      window.location.hash || ''
    }`;
    window.history.replaceState(null, '', next);
    // 依赖用 key 串：调用方常内联写默认值对象，用对象引用会每次都执行
  }, [filters, keyStr]);

  /* 浏览器前进/后退 → 回读 URL */
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onPop = () => setFilters(readFromUrl(defaults, keys));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [keyStr]);

  const setFilter = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const setAll = useCallback((patch) => {
    setFilters((prev) => ({ ...prev, ...patch }));
  }, []);

  const reset = useCallback(() => setFilters({ ...defaults }), []);

  return { filters, setFilter, setAll, reset };
};

/** 从当前 URL 读值 */
function readFromUrl(defaults, keys) {
  const result = { ...defaults };
  if (typeof window === 'undefined') return result;
  const params = new URLSearchParams(window.location.search);
  keys.forEach((key) => {
    if (params.has(key)) result[key] = params.get(key);
  });
  return result;
}

/**
 * 防抖值（输入类筛选用，300ms）
 * 页面里现在各自手写「keyword 输入值 / search 提交值」两份 state，
 * 统一用这个 hook，行为一致且少一处状态。
 *
 * @param value 原始值（输入框的值）
 * @param delay 防抖毫秒，默认 300
 */
export const useDebouncedValue = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value);
  const timer = useRef(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setDebounced(value), delay);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [value, delay]);

  return debounced;
};

export default useUrlFilters;
